import { Trans } from "react-i18next"; import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ChevronRight, ClipboardCheck } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageHeader, LoadingState } from "../../../components/ui";
import {
  fetchMyCreatedAnnouncements,
  fetchPublishedAnnouncements,
  fetchAnnouncementById,
  fetchDepartments,
  fetchManagers,
  sortAnnouncementsForDisplay,
  toLocalISODate
} from
  "../../../services/announcementServices";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { useIsHR } from "../hooks/useIsHR";
import { AddAnnouncementModal } from "../components/AddAnnouncementModal";
import { StatusPill } from "../components/StatusPill";
import { Toast } from "../components/Toast";
import { ROUTES, IMPORTANT_COLOR, GENERAL_COLOR, toUiStatus, formatDate } from "../constants/announcementOptions";

const PAGE_SIZE = 10;

function buildExtra(apiStatus, remarks, publishedDate) {
  const s = (apiStatus ?? "").toLowerCase();
  if (s === "published") return { type: "published-date", date: publishedDate ?? "" };
  if (s === "required corrections") return { type: "remarks", heading: "HR Remarks", text: remarks ?? "", cta: "Edit & Resubmit" };
  if (s === "draft") return { type: "draft", cta: "Continue Draft" };
  return null;
}

function normaliseItem(item) {
  const publishedDate = item.announcementStatus === "Published" && item.updatedOn ? formatDate(item.updatedOn) : "";
  const rawExpiry = item.archiveDate ?? item.announcementArchiveDate ?? item.expiryOn ?? item.endDate ?? item.validUntil ?? null;
  const rawPublish = item.publishDate ?? item.announcementPublishDate ?? item.publishOn ?? null;
  const isImportant = (item.announcementType ?? "").toLowerCase() === "important";
  return {
    id: String(item.announcementId),
    announcementId: item.announcementId,
    title: item.announcementTitle ?? "",
    type: item.announcementType ?? "",
    priority: item.announcementType ?? "General",
    priorityColor: isImportant ? IMPORTANT_COLOR : GENERAL_COLOR,
    barColor: isImportant ? IMPORTANT_COLOR : GENERAL_COLOR,
    createdDate: formatDate(item.createdOn),
    publishedDate,
    archiveDate: formatDate(rawExpiry),
    archiveDateISO: toLocalISODate(rawExpiry),
    publishDateISO: toLocalISODate(rawPublish),
    audience: item.recipients ?? "",
    departmentId: item.departmentId ?? 0,
    managerId: item.managerId ?? 0,
    departmentName: item.departmentName ?? item.deptName ?? item.department_name ?? null,
    managerName: item.managerName ?? item.managerFirstName ?? item.manager_name ?? null,
    status: toUiStatus(item.announcementStatus),
    content: item.announcementBody ?? "",
    remarks: item.remarks ?? "",
    hasSupportingDocument: item.hasSupportingDocument ?? false,
    supportingDocumentBase64: item.supportingDocumentBase64 ?? "",
    extra: buildExtra(item.announcementStatus, item.remarks, publishedDate)
  };
}

function AnnouncementCard({ item, onResubmit, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      className="flex items-center gap-4 bg-white rounded-xl px-6 py-5 border-l-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_14px_rgba(30,51,131,0.12)] hover:-translate-y-px transition cursor-pointer"
      style={{ borderLeftColor: item.barColor }}>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-[18px] font-medium text-foreground truncate">{item.title}</h3>
          <StatusPill status={item.status} />
        </div>

        <div className="flex items-center gap-2.5 mt-2.5 text-sm text-foreground flex-wrap">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: item.priorityColor }} />
          <span className="font-medium">{item.priority}</span>
          <span className="pl-2.5 border-l border-(--color-text-border)"><Trans i18nKey="dateTime.createdOn" /> {item.createdDate}</span>
          <span className="pl-2.5 border-l border-(--color-text-border)">
            {item.audience}
            {item.audience === "Department" && item.departmentName ? ` — ${item.departmentName}` : ""}
            {item.audience === "Reportees" && item.managerName ? ` — Mgr: ${item.managerName}` : ""}
          </span>
        </div>

        <p className="mt-3.5 text-sm leading-relaxed text-foreground line-clamp-2 wrap-break-word">{item.content}</p>

        {item.extra?.type === "published-date" &&
          <p className="mt-2.5 text-[12px] font-medium text-success flex items-center gap-1.5">
            <Trans i18nKey="dateTime.publishedOn" /> {item.extra.date || "—"}
          </p>
        }

        {item.extra?.type === "remarks" &&
          <div className="mt-4 bg-(--status-warning-bg) rounded-lg px-5 py-4" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-medium text-foreground mb-1.5">{item.extra.heading}</p>
            <p className="text-sm text-[#44403C] leading-relaxed mb-3.5">{item.extra.text}</p>
            <button type="button" onClick={() => onResubmit?.(item)}
              className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-accent">
              {item.extra.cta}
            </button>
          </div>
        }

        {item.extra?.type === "draft" &&
          <div className="mt-4" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => onResubmit?.(item)}
              className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-accent">
              {item.extra.cta}
            </button>
          </div>
        }
      </div>
      <ChevronRight className="h-5 w-5 per-file-hint shrink-0" />
    </div>);

}

export function CreatorAnnouncementsPage() {
  const navigate = useNavigate();
  const { isHR, loading: roleLoading } = useIsHR();
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [resubmitLoading, setResubmitLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [page, setPage] = useState(1);

  const loadList = (forHR = false) => {
    setLoadingList(true);
    const fetcher = forHR ?
      () => Promise.all([
        fetchPublishedAnnouncements(),
        fetchMyCreatedAnnouncements().catch(() => [])]
      ).then(([published, mine]) => [
        ...mine.filter((item) => item.announcementStatus !== "Published"),
        ...published]
      ) :
      fetchMyCreatedAnnouncements;
    fetcher()
      .then(async (raw) => {
        const normalised = sortAnnouncementsForDisplay(raw).map(normaliseItem);
        const hasDept = normalised.some((i) => i.audience === "Department" && i.departmentId > 0);
        const hasMgr = normalised.some((i) => i.audience === "Reportees" && i.managerId > 0);
        let deptMap = {}, mgrMap = {};
        if (hasDept) {
          const d = await fetchDepartments().catch(() => []);
          deptMap = Object.fromEntries(d.map((x) => [x.departmentId, x.departmentName]));
        }
        if (hasMgr) {
          const m = await fetchManagers().catch(() => []);
          mgrMap = Object.fromEntries(m.map((x) => [x.managerId, x.managerName]));
        }
        setItems(normalised.map((i) => ({
          ...i,
          departmentName: i.departmentName ?? (i.audience === "Department" && i.departmentId > 0 ? deptMap[i.departmentId] ?? null : null),
          managerName: i.managerName ?? (i.audience === "Reportees" && i.managerId > 0 ? mgrMap[i.managerId] ?? null : null)
        })));
      })
      .catch((err) => setListError(err.status === 401 ?
        "Session expired. Please refresh the page and log in again." :
        `Failed to load announcements: ${err.message}`))
      .finally(() => setLoadingList(false));
  };

  useEffect(() => {
    if (!roleLoading) loadList(isHR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleLoading]);

  const handleResubmit = async (item) => {
    setResubmitLoading(true);
    try {
      const detail = await fetchAnnouncementById(item.announcementId ?? item.id);
      setEditItem(normaliseItem(detail));
    } catch {
      setEditItem(item);
    } finally {
      setResubmitLoading(false);
      setModalOpen(true);
    }
  };

  const title = isHR ? "Organization Announcements" : "My Announcements";

  const totalPages = Math.max(Math.ceil(items.length / PAGE_SIZE), 1);
  const safePage = Math.min(page, totalPages);
  const visible = items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <AppLayout>
      <PageHeader back={<PageBackLink to="/" />}
        title={title}
        action={
          <div className="flex items-center gap-2">
            {isHR &&
              <button type="button" onClick={() => navigate(ROUTES.hrReview)}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-(--color-text-border) bg-white text-primary font-semibold text-sm hover:bg-workspace transition cursor-pointer">
                <ClipboardCheck className="h-4 w-4" /> <Trans i18nKey="announcements.reviewRequests" />
              </button>
            }
            <button type="button" onClick={() => { setEditItem(null); setModalOpen(true); }}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-accent transition cursor-pointer">
              <Plus className="h-4 w-4" /> <Trans i18nKey="announcements.newAnnouncement" />
            </button>
          </div>
        } />


      <div className="max-w-[1050px] flex flex-col gap-4">
        {loadingList ?
          <LoadingState /> :
          listError ?
            <p className="p-6 text-sm text-error">{listError}</p> :
            items.length === 0 ?
              <p className="p-6 text-sm per-file-hint"><Trans i18nKey="announcements.noAnnouncementsYet" /></p> :

              <>
                {visible.map((item) =>
                  <AnnouncementCard
                    key={item.id}
                    item={item}
                    onResubmit={handleResubmit}
                    onClick={() => navigate(ROUTES.creatorDetail(item.id), { state: { item } })} />

                )}
                <div className="mt-4">
                  <TablePagination
                    page={safePage}
                    totalPages={totalPages}
                    totalItems={items.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage} />
                </div>
              </>
        }
      </div>

      {resubmitLoading &&
        <div className="fixed inset-0 z-60 bg-black/25 grid place-items-center">
          <span className="bg-white px-7 py-4 rounded-xl text-sm text-foreground"><Trans i18nKey="announcements.loadingAnnouncement" /></span>
        </div>
      }

      {modalOpen &&
        <AddAnnouncementModal
          open={modalOpen}
          initialData={editItem}
          isHR={isHR}
          onClose={(refresh, message) => {
            setModalOpen(false);
            setEditItem(null);
            if (refresh) { loadList(isHR); if (message) setSuccessMsg(message); }
          }} />

      }

      {successMsg &&
        <Toast tone="success" onDone={() => setSuccessMsg("")}>✓ {successMsg}</Toast>
      }
    </AppLayout>);

}
